import React, { FunctionComponent, PropsWithChildren, useRef, useState } from "react";

import { bemClassNames, Key } from "@multiweb/reactUtils";
import { ButtonBackgroundColor } from "../ButtonProvider";
import { ModalWindow, ModalWindowProps } from "../ModalWindow";
import { TextColor } from "../Text";
import { TextButton } from "../TextButton";
import { ComponentSize } from "../types";

export type MessageWindowComponentProps = PropsWithChildren<{
    /** text předaný primary button (levé tlačítko) */
    readonly secondaryButtonText?: string;
    /** text předaný secondary button (pravé tlačítko) */
    readonly primaryButtonText: string;
    /** callback funkce volaná při kliku na primary button */
    readonly onPrimaryButtonClick?: () => void;
    /** callback funkce volaná při kliku na secondary button */
    readonly onSecondaryButtonClick?: () => void;
    /** callback funkce volaná při zavření MessageWindow */
    readonly onCloseMessageWindow: () => void;
}>
    & Pick<ModalWindowProps, "closeOnEscape">;

const classNames = bemClassNames("message-window");
const messageWindowClassName = classNames();
const messageWindowContentClassName = classNames("content");
const buttonsAreaClassName = classNames("buttons-area");

export const MessageWindowComponent: FunctionComponent<MessageWindowComponentProps> = ({
    children,
    secondaryButtonText,
    primaryButtonText,
    closeOnEscape,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
    onCloseMessageWindow,
}) => {
    const [isDisplayed, setIsDisplayed] = useState(true);
    const focusButtonRef = useRef<HTMLButtonElement>(null);

    const handleOnPrimaryButtonClick = () => {
        setIsDisplayed(false);
        if (onPrimaryButtonClick)
            onPrimaryButtonClick();
    };

    const handleOnSecondaryButtonClick = () => {
        setIsDisplayed(false);
        if (onSecondaryButtonClick)
            onSecondaryButtonClick();
    };

    const focusDialog = () => {
        focusButtonRef.current?.focus();
    };

    return (
        <ModalWindow {...{ isDisplayed, closeOnEscape }} onCloseChange={onCloseMessageWindow} onContentFocus={focusDialog} defaultWidth={640} defaultHeight={134} minWidth={320} minHeight={134} maxHeight={900} maxWidth={1200}>
            <div className={messageWindowClassName}>
                <div className={messageWindowContentClassName}>
                    {children}
                </div>
                <div className={buttonsAreaClassName}>
                    {secondaryButtonText &&
                        <TextButton
                            size={ComponentSize.large}
                            onClick={handleOnSecondaryButtonClick}
                            text={secondaryButtonText}
                            title={secondaryButtonText}
                            backgroundColor={ButtonBackgroundColor.transparent}
                            color={TextColor.colorPrimary}
                        />
                    }
                    <TextButton
                        ref={focusButtonRef}
                        size={ComponentSize.large}
                        isFocused
                        onClick={handleOnPrimaryButtonClick}
                        onKeyDown={(e) => e.key === Key.Tab && !e.shiftKey ? e.preventDefault() : undefined}
                        text={primaryButtonText}
                        title={primaryButtonText}
                        backgroundColor={ButtonBackgroundColor.colorPrimary}
                        color={TextColor.whiteBlack}
                    />
                </div>
            </div>
        </ModalWindow>
    );
};
